import { lazy, Suspense, useEffect, useState } from "react";
import type React from "react";
import FoerderRechner from "./components/foerder-rechner/FoerderRechner";
import ROISimulator from "./components/roi-simulator/ROISimulator";
import NEKOHeader from "./components/shared/NEKOHeader";
import LoadingSpinner from "./components/shared/LoadingSpinner";
import LandingPage from "./pages/LandingPage";
import PVLandingPage from "./pages/PVLandingPage";
import WaermepumpeLandingPage from "./pages/WaermepumpeLandingPage";
import ImpressumPage from "./pages/ImpressumPage";
import DatenschutzPage from "./pages/DatenschutzPage";
// import TenantFooter from "./components/shared/TenantFooter";
import PreviewBanner from "./components/shared/PreviewBanner";

const SolarRoofMapper = lazy(
  () => import("./components/dach-mapper/SolarRoofMapper")
);

export type ActiveTool = "foerder-rechner" | "roi-simulator" | "dach-mapper";

type View = "landing" | "pv" | "waermepumpe" | "widget" | "impressum" | "datenschutz";

function getView(): View {
  const p = new URLSearchParams(window.location.search);
  const path = window.location.pathname;
  // Pathname-based routing (TenantFooter fallback links use /impressum, /datenschutz)
  if (path === "/impressum") return "impressum";
  if (path === "/datenschutz") return "datenschutz";
  // Query-param routing (existing pattern)
  if (p.has("tool") || p.get("embed") === "true") return "widget";
  if (p.get("page") === "pv") return "pv";
  if (p.get("page") === "waermepumpe") return "waermepumpe";
  if (p.get("page") === "impressum") return "impressum";
  if (p.get("page") === "datenschutz") return "datenschutz";
  return "landing";
}

function getBgStyle(): React.CSSProperties {
  const bg = new URLSearchParams(window.location.search).get("bg");
  if (bg === "light") return { backgroundColor: "#ffffff" };
  if (bg === "dark") return { backgroundColor: "#212a2b" };
  if (bg?.startsWith("#")) return { backgroundColor: bg };
  return {};
}

function getInitialTool(): ActiveTool {
  const p = new URLSearchParams(window.location.search);
  const tool = p.get("tool");
  if (tool === "roi-simulator") return "roi-simulator";
  if (tool === "dach-mapper") return "dach-mapper";
  return "foerder-rechner";
}

export default function App() {
  const view = getView();
  const [activeTool, setActiveTool] = useState<ActiveTool>(getInitialTool);

  // Light-mode theme activation — sets data-theme on <html> so light-theme.css overrides apply.
  useEffect(() => {
    const bg = new URLSearchParams(window.location.search).get("bg");
    if (bg !== "light") return;
    document.documentElement.setAttribute("data-theme", "light");
    return () => document.documentElement.removeAttribute("data-theme");
  }, []);

  if (view === "impressum") return <ImpressumPage />;
  if (view === "datenschutz") return <DatenschutzPage />;
  if (view === "pv") return <PVLandingPage />;
  if (view === "waermepumpe") return <WaermepumpeLandingPage />;
  if (view === "landing") return <LandingPage />;

  // Widget-Modus für iframe-Einbettung (?tool=... oder ?embed=true)
  return (
    <div className="bg-neko-dark flex flex-col" style={getBgStyle()}>
      <PreviewBanner />
      <NEKOHeader activeTool={activeTool} onToolChange={setActiveTool} />
      <main className="max-w-2xl mx-auto px-4 py-6 w-full">
        {activeTool === "foerder-rechner" && <FoerderRechner onToolChange={setActiveTool} />}
        {activeTool === "roi-simulator" && <ROISimulator />}
        {activeTool === "dach-mapper" && (
          <Suspense fallback={<LoadingSpinner text="Karte wird geladen…" />}>
            <SolarRoofMapper onToolChange={setActiveTool} />
          </Suspense>
        )}
      </main>
      {/* <TenantFooter /> */}
    </div>
  );
}
